import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import logoSrc from "@/assets/logo.png";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({ meta: [{ title: "Reset Password — Code Cubs Academy" }] }),
  component: ForgotPasswordPage,
});

const schema = z.object({
  email: z.string().trim().email(),
});

function ForgotPasswordPage() {
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = schema.safeParse({
      email: String(fd.get("email") ?? ""),
    });
    if (!parsed.success) return toast.error("Please enter a valid email address.");

    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
      redirectTo: `${window.location.origin}/update-password`,
    });
    setBusy(false);

    if (error) {
      return toast.error(error.message);
    }

    setSuccess(true);
    toast.success("Password reset link sent to your email.");
  };

  return (
    <div className="container mx-auto px-4 py-16 max-w-md">
      <Card className="rounded-3xl border-0 shadow-soft">
        <CardContent className="p-8">
          <div className="flex justify-center mb-6">
            <img src={logoSrc} alt="Code Cubs Academy" className="h-32 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-extrabold text-center mb-2">Reset Password 🔒</h1>

          {success ? (
            <div className="text-center space-y-4">
              <p className="text-muted-foreground">
                We've sent a password reset link to your email address. Please check your inbox.
              </p>
              <Button
                asChild
                className="w-full rounded-full h-12 bg-gradient-primary text-primary-foreground border-0"
              >
                <Link to="/login">Return to Login</Link>
              </Button>
            </div>
          ) : (
            <>
              <p className="text-center text-muted-foreground mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" name="email" type="email" required />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full h-12 bg-gradient-primary text-primary-foreground border-0"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reset Link"}
                </Button>
              </form>
            </>
          )}

          <p className="text-center text-sm mt-6 text-muted-foreground">
            Remember your password?{" "}
            <Link to="/login" className="text-primary font-semibold">
              Log in here
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
